"use client";
import { DrupalNode } from "next-drupal";
import React from "react";
import { IoMdImages } from "react-icons/io";
import { motion } from "framer-motion";
import Link from "next/link";
import { config } from "@/lib/config";
import Image from "next/image";
import HeadingAndDescription from "@/components/sections/HeadingAndDescription";
import { resolveLink } from "@/utils";

interface CardWithImageAndIconProps {
  data: DrupalNode;
}

const CardWithImageAndIcon: React.FC<CardWithImageAndIconProps> = ({
  data,
}) => {
  const isBlue = data?.field_background_color === "blue";
  const isWhite = data?.field_background_color === "white";
  const isGray = data?.field_background_color === "gray";
  const isDarkDray = data?.field_background_color === "dark_gray";

  const bgClass = isBlue
    ? "bg-[#003350]"
    : isWhite
      ? "bg-white"
      : isGray
        ? "bg-[#f9f9f9]"
        : isDarkDray
          ? "bg-mapGray"
          : "bg-white";

  return (
    <div
      className={`${bgClass} betweenMobileTab:py-16 mobileMax:py-10 relative py-[120px]`}
    >
      <div className="mini-container relative z-[2]">
        <HeadingAndDescription
          link={data?.field_button_link}
          title={data?.field_title}
        />

        <div
          className={`max-w-[880px] mx-auto mobileMax:order-3 mobileMax:mb-5 text-center text-small ${
            isBlue ? "text-white" : "text-[#003350]"
          } font-poppins font-normal mobileMax:text-xsmall leading-normal mt-10`}
          dangerouslySetInnerHTML={{
            __html: data?.field_description?.processed || "",
          }}
        />

        <div className="flex flex-wrap justify-center items-stretch box-border lieTablets:justify-start mt-[20px]">
          {data?.field_add_card?.map((card: DrupalNode) => {
            const link = resolveLink(card?.field_links?.[0]?.uri);
            const isLink = link && link !== "#";

            const CardContent = (
              <motion.div
                className="bg-white flex flex-col h-full rounded-lg shadow-md hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                  border: {
                    duration: 1.8,
                    ease: "easeInOut",
                  },
                }}
              >
                {card?.field_image?.uri?.url ? (
                  <Image
                    src={`${config.apiBase}${card?.field_image?.uri?.url}`}
                    alt={card?.resourceIdObjMeta?.alt || "Card image"}
                    width={600}
                    height={300}
                    className="w-full h-56 object-cover rounded-t-lg"
                  />
                ) : (
                  <div className="w-full h-56 bg-placeholder flex items-center justify-center">
                    <IoMdImages className="text-white w-[60%] h-[80px]" />
                  </div>
                )}

                <div className="px-5 py-10 mobileMax:py-5 text-center">
                  {card?.field_icon?.uri?.url && (
                    <div className="mb-5 flex justify-center items-center h-16">
                      <img
                        src={`${config.apiBase}${
                          card?.field_icon?.uri?.url || ""
                        }`}
                        alt={card?.resourceIdObjMeta?.alt || "Card image"}
                        className="h-full"
                      />
                    </div>
                  )}

                  <p className="font-bold text-[20px] mobileMax:text-xsmall text-transparent bg-clip-text bg-[linear-gradient(to_right,#24728F,#11375C)] cursor-default">
                    {card?.field_title}
                  </p>
                </div>
              </motion.div>
            );

            return isLink ? (
              <Link
                key={card?.id}
                href={link}
                className="cursor-pointer px-[15px] mobileMax:px-0 mt-[30px] w-[33%] lieTablets:w-[50%] mobileMax:w-full tab:hover:scale-105 transition-all duration-700 flex-shrink-0"
              >
                {CardContent}
              </Link>
            ) : (
              <div
                key={card?.id}
                className="px-[15px] mobileMax:px-0 mt-[30px] w-[33%] lieTablets:w-[50%] mobileMax:w-full transition-all duration-700 flex-shrink-0"
              >
                {CardContent}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CardWithImageAndIcon;
